import React, { useState, useEffect, useMemo } from "react";
import GenericDraggableTableView from "../../../Features/Table/GenericDraggableTable";
import { useTranslation } from "react-i18next";
import { getColumnsConfig } from "../../../Features/Table/GenericDraggableTable/GenericDraggableTableFuncations";
import Loader from "../../../Helper/Loaders/Loader";
import { MAX_COLUMN_SELECTION_COUNT } from "../../../Helper/DigitalTwin/DataFile";
import { getDiagnosticsAnalyticsListAction, getDiagnosticsAnalyticsList } from "../../../Api/BmsFaultsApi";
import { formatDateForAPI } from "../../../Helper/Analytics/AnalyticsHelper";
import { CHART_TYPES } from "./chartConfig";
import { usePagesContent } from "../../../Hooks/usePagesContent";
import DeviceIDLink from "../../../Features/Table/GenericDraggableTable/Renderers/columnTypes/DeviceIDLink";
import { encryptTheParams } from "../../../Helper/QueryParams/EncryptDecrypt";
import { useNavigate } from "react-router-dom";
import { useBmsMetaInfoFields, useBmsMetaReady, useBmsTableConfig } from "./useBmsTableConfig";

// Transform severity data from API response to table format
const transformSeverityData = (listData) => {
  return listData.map((item) => {
    const row = {
      id: item.xLabel,
      busNumber: item.xLabel || '',
      ...item.metaInfo,
    };

    // Add metrics from metricsArray
    if (item.metricsArray && Array.isArray(item.metricsArray)) {
      item.metricsArray.forEach(metric => {
        const severityType = (metric.type || '').toLowerCase();
        if (severityType === 'warning' || severityType === 'warn') {
          row['warning'] = metric.value || 0;
        } else if (severityType === 'high severity' || severityType === 'high' || severityType === 'highseverity') {
          row['high severity'] = metric.value || 0;
        } else if (severityType === 'critical' || severityType === 'crit') {
          row['critical'] = metric.value || 0;
        }
      });
    }

    // Ensure all severity fields exist
    if (!row.warning) row.warning = 0;
    if (!row['high severity']) row['high severity'] = 0;
    if (!row['critical']) row['critical'] = 0;

    return row;
  });
};

// Transform auto-resolved data from API response to table format
const transformAutoResolvedData = (listData) => {
  return listData.map((item) => {
    const row = {
      id: item.xLabel,
      busNumber: item.xLabel || '',
      ...item.metaInfo,
    };

    // Add metrics from metricsArray
    if (item.metricsArray && Array.isArray(item.metricsArray)) {
      item.metricsArray.forEach(metric => {
        const resolutionType = (metric.type || '').toLowerCase();
        if (resolutionType === 'autoresolved' || resolutionType === 'auto-resolved') {
          row['autoresolved'] = metric.value || 0;
        } else if (resolutionType === 'manualresolved' || resolutionType === 'manual-resolved' || resolutionType === 'manually resolved') {
          row['manualresolved'] = metric.value || 0;
        }
      });
    }

    // Ensure all resolution fields exist
    if (!row['autoresolved']) row['autoresolved'] = 0;
    if (!row['manualresolved']) row['manualresolved'] = 0;

    return row;
  });
};

// The bmsAlertV2 org meta key for each chart type's details page table. Mirrors
// PlatformAlertsV2, where the same two tables carry the same names.
const DETAILS_TABLE_KEY_BY_CHART = {
  [CHART_TYPES.SEVERITY]: "severityDetailsPageTable",
  [CHART_TYPES.AUTO_RESOLVED]: "autoResolvedDetailsPageTable",
};

// Get table-specific configuration
const getTableConfig = (chartType) => {
  const configs = {
    [CHART_TYPES.SEVERITY]: {
      select: ['GeneratedFaultsCount'],
      groupBy: ['batteryID', 'FaultSeverityName'],
      transformData: transformSeverityData
    },
    [CHART_TYPES.AUTO_RESOLVED]: {
      select: ['ResolvedFaultCount'],
      groupBy: ['batteryID', 'ResolutionType'],
      transformData: transformAutoResolvedData
    }
  };
  return configs[chartType] || configs[CHART_TYPES.SEVERITY];
};

// Build common API params object
const buildApiParams = (tableConfig, commonParams) => {
  const params = {
    startDate: formatDateForAPI(commonParams.startDate),
    endDate: formatDateForAPI(commonParams.endDate),
    select: tableConfig.select,
    filters: [...commonParams.appliedFilters, ...commonParams.searchFilters],
    groupBy: tableConfig.groupBy,
    granularity: null,
    metaInfo: commonParams.metaInfo,
    pageNum: commonParams.pageNumber,
    pageCount: commonParams.rowsPerPage,
    skipPagination: false,
  };

  if (commonParams.sortBy.length > 0) {
    params.sortBy = JSON.stringify([...commonParams.sortBy]);
  }

  return params;
};

// Unified fetch function for table data
const fetchTableData = async (chartType, tableConfig, commonParams, globalFilters) => {
  const params = buildApiParams(tableConfig, commonParams);
  const response = await getDiagnosticsAnalyticsList(globalFilters, params);
  const listData = response?.response?.data || [];
  const responseTotal = response?.response?.total?.total || listData.length;
  const transformedData = tableConfig.transformData(listData);
  return { transformedData, responseTotal };
};

const ChartDetailsTableSection = ({ config, title, emptyMessage, startDate, endDate, chartType, appliedFilters, searchTerm, hierarchicalLocation, fleetIds, onboardingLocations, allOnboardingLocations, alertCategories, operationalStatus }) => {
  // Columns come from the bmsAlertV2 org meta when configured, chartConfig otherwise.
  // Memoised because both feed dependency arrays below.
  const metaReady = useBmsMetaReady();
  const resolveTable = useBmsTableConfig();
  const resolveMetaInfo = useBmsMetaInfoFields();
  const detailsTableKey =
    DETAILS_TABLE_KEY_BY_CHART[chartType] || DETAILS_TABLE_KEY_BY_CHART[CHART_TYPES.SEVERITY];
  const resolvedTable = useMemo(
    () => resolveTable(detailsTableKey, config),
    [resolveTable, detailsTableKey, config]
  );
  const metaInfoFields = useMemo(
    () => resolveMetaInfo(config.metaInfo, detailsTableKey, config),
    [resolveMetaInfo, detailsTableKey, config]
  );

  const { t } = useTranslation();

  const pagesContent = usePagesContent();
  const navigate = useNavigate();

  const [initialColumns, setInitialColumns] = useState([]);
  const [tableColumns, setTableColumns] = useState([]);
  const [selectedColumnKeys, setSelectedColumnKeys] = useState([]);
  const [defaultSelectedColumnKeys, setDefaultSelectedColumnKeys] = useState([]);
  const [totalSelectedRowKeys, setTotalSelectedRowKeys] = useState(new Set());
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [columnOrder, setColumnOrder] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortBy, setSortBy] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [total, setTotal] = useState(0);

  // Helper function to prepare search filters
  const prepareSearchFilters = () => {
    const searchID = searchTerm.selectedSearchColumn || "searchID";
    return (searchTerm?.selectedDevices?.length > 0)
      ? [{ name: searchID, values: searchTerm.selectedDevices }]
      : [];
  };

  // Helper function to build common filter structure
  const buildFilters = () => {
    const searchFilters = prepareSearchFilters();
    const baseFilters = {
      startDate: formatDateForAPI(startDate),
      endDate: formatDateForAPI(endDate),
      granularity: null,
      metaInfo: metaInfoFields,
      columns: tableColumns,
      columnOrder,
      resourceIDs: Array.from(totalSelectedRowKeys),
      selectAll: isSelectAll,
      ...(sortBy.length > 0 && { sortBy })
    };

    if (chartType === CHART_TYPES.SEVERITY) {
      return {
        ...baseFilters,
        select: ['GeneratedFaultsCount'],
        filters: [...appliedFilters, ...searchFilters],
        groupBy: ['batteryID', 'FaultSeverityName'],
      };
    } else if (chartType === CHART_TYPES.AUTO_RESOLVED) {
      return {
        ...baseFilters,
        select: ['ResolvedFaultCount'],
        filters: [...appliedFilters, ...searchFilters],
        groupBy: ['batteryID', 'ResolutionType'],
      };
    }
    return baseFilters;
  };

  // Helper function to build global filters (same as parent component). An absent
  // fleetIds sends no fleetIDs header, which is how this table has always behaved.
  const buildGlobalFilters = () => ({
    hierarchicalLocation: hierarchicalLocation,
    fleets: fleetIds,
    onboardingLocations,
    allOnboardingLocations,
    alertCategories: alertCategories,
    operationalStatus: operationalStatus
  });

  // Helper function to call API with action type
  const callDiagnosticsApi = async (actionType, additionalParams = {}) => {
    const filters = buildFilters();
    const globalFilters = buildGlobalFilters();
    const res = await getDiagnosticsAnalyticsListAction(globalFilters, {
      actionType,
      ...additionalParams,
      ...filters
    });

    if (res && res.responseStatus.code === 200) {
      return res.response.data;
    }
    return [];
  };

  function onBatteryIDClick(record) {
    let temp;
    temp = {
      batteryID: record.id,
      deviceID: record.deviceID,
      batterySearchedGlobal: "false",
      prevLink: '/',
      navigateFromTab: true,
      tabValue: 'alerts'
    };

    encryptTheParams(temp, navigate, true);
  }

  const customRenderers = {
    'device-id-link': (record, config) => {
      return <DeviceIDLink
        record={record}
        config={config}
        onTextClick={() => onBatteryIDClick(record)}
      />
    },
  };

  const setupTableColumns = () => {
    const availableColumns = getColumnsConfig({
      tableColumns: resolvedTable.tableColumnsConfig,
      tableColumnOrder: resolvedTable.tableColumnOrder,
      customRenderers: customRenderers,
      t,
    });

    setInitialColumns(availableColumns);
    const defaultSelected = availableColumns.slice(0, MAX_COLUMN_SELECTION_COUNT);
    const defaultKeys = defaultSelected.map(col => col.dataIndex);
    setTableColumns(defaultSelected);
    setSelectedColumnKeys(defaultKeys);
    setDefaultSelectedColumnKeys(defaultKeys);
  };

  const getCSVData = async () => {
    return await callDiagnosticsApi("download");
  };

  const getCSVFileName = () => {
    let fileName = 'BmsFaults';
    if (chartType === CHART_TYPES.SEVERITY) {
      fileName += '_FaultSeverity'
    } else if (chartType === CHART_TYPES.AUTO_RESOLVED) {
      fileName += '_FaultResolution';
    }
    const formattedStartDate = new Date(startDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    const formattedEndDate = new Date(endDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

    return `${fileName}_${formattedStartDate}-${formattedEndDate}.csv`
  };

  const onSendEmail = async ({ sendTo, sendCc, subject, body }) => {
    return await callDiagnosticsApi("mail", {
      emailTo: sendTo,
      emailCc: sendCc,
      subject: subject,
      body: body,
    });
  };

  const onSelectChange = (record, selected) => {
    const updatedSelectedKeys = new Set(totalSelectedRowKeys);
    if (selected) {
      updatedSelectedKeys.add(record.id);
    } else {
      updatedSelectedKeys.delete(record.id);
      if (isSelectAll) {
        setIsSelectAll(false);
      }
    }
    setTotalSelectedRowKeys(updatedSelectedKeys);
  };

  const onSelectAllChange = (selected, selectedRows) => {
    setIsSelectAll(selected);
    setTotalSelectedRowKeys(
      selected ? new Set(selectedRows.map((row) => row?.id)) : new Set()
    );
  };

  const getTotalRowSelection = () => Array.from(totalSelectedRowKeys);

  const getTotalRowSelectionCount = () =>
    isSelectAll ? total : totalSelectedRowKeys.size;

  const getCurrentPageSelection = () => Array.from(totalSelectedRowKeys);

  const onColumnOrderChange = (newTableColumns) => {
    setColumnOrder(newTableColumns.map((col) => col.headerLabel));
  };

  const applySelectedColumns = (newSelectedKeys) => {
    const filteredColumns = initialColumns.filter((columnConfig) =>
      newSelectedKeys.includes(columnConfig.dataIndex)
    );

    setTableColumns(filteredColumns);
    setSelectedColumnKeys(newSelectedKeys);
    setColumnOrder(filteredColumns.map((col) => col.headerLabel));
  };

  const handlePageChange = (page) => {
    setTotalSelectedRowKeys(new Set());
    setIsSelectAll(false);
    setPageNumber(page);
  };

  const handleSortChange = (key, order) => {
    const direction = order === "asc" ? "ASC" : "DESC";
    const newSortBy = [{ column: key, direction }];
    setSortBy(newSortBy);
    setPageNumber(1);
    setTotalSelectedRowKeys(new Set());
    setIsSelectAll(false);
  };

  const handleClearSelection = () => {
    setTotalSelectedRowKeys(new Set());
    setIsSelectAll(false);
  };


  // Get current sorting state for initialSorting prop
  // Create a new object on each render to ensure the table's useEffect detects changes
  // This matches the pattern used in InventoryList.jsx
  const getCurrentSorting = () => {
    if (sortBy && sortBy.length > 0) {
      const firstSort = sortBy[0];
      return {
        sortBy: firstSort.column,
        orderBy: firstSort.direction.toLowerCase()
      };
    }
    return {};
  };

  useEffect(() => {
    setupTableColumns();
  }, [resolvedTable]);

  // Reset pagination and sorting when filters/search/date changes.
  //
  // sortBy is cleared through an updater that returns the previous array when it
  // is already empty. A fresh [] would be a new identity, and sortBy is in the
  // fetch effect's dependencies, so an unconditional clear fired a second
  // identical list request on mount and on every filter change.
  useEffect(() => {
    setPageNumber(1);
    setSortBy((prev) => (prev.length ? [] : prev));
  }, [chartType, startDate, endDate, appliedFilters, hierarchicalLocation, fleetIds, onboardingLocations, allOnboardingLocations, alertCategories, operationalStatus, searchTerm]);

  // Fetch table data
  useEffect(() => {
    const loadTableData = async () => {
      // Wait for the org meta to settle before the first fetch. It decides which
      // metaInfo this request asks for, so a fetch that beats it asks for nothing
      // and paints em dashes before repainting.
      if (!metaReady) return;
      if (!startDate || !endDate) {
        return;
      }

      try {
        setIsLoading(true);

        const globalFilters = {
          hierarchicalLocation: hierarchicalLocation,
          fleets: fleetIds,
          onboardingLocations,
          allOnboardingLocations,
          alertCategories: alertCategories,
          operationalStatus: operationalStatus
        };

        const searchID = searchTerm.selectedSearchColumn || "searchID";
        const searchFilters = (searchTerm?.selectedDevices?.length > 0)
          ? [{
            name: searchID,
            values: searchTerm.selectedDevices
          }]
          : [];

        const tableConfig = getTableConfig(chartType);
        const commonParams = {
          startDate,
          endDate,
          appliedFilters,
          searchFilters,
          metaInfo: metaInfoFields,
          pageNumber,
          rowsPerPage,
          sortBy
        };

        const { transformedData, responseTotal } = await fetchTableData(
          chartType,
          tableConfig,
          commonParams,
          globalFilters
        );

        setTableData(transformedData);
        setTotal(responseTotal);
      } catch (error) {
        console.error("Error fetching table data:", error);
        setTableData([]);
        setTotal(0);
      } finally {
        setIsLoading(false);
      }
    };

    loadTableData();
  }, [chartType, startDate, endDate, appliedFilters, hierarchicalLocation, fleetIds, onboardingLocations, allOnboardingLocations, alertCategories, operationalStatus, searchTerm, pageNumber, rowsPerPage, sortBy, metaInfoFields, metaReady]);


  return (
    <GenericDraggableTableView
      title={title}
      showPrimaryButton={false}
      count={total}
      tableColumns={tableColumns}
      selectedColumns={selectedColumnKeys}
      tableData={tableData}
      availableEmailDomains={pagesContent?.metaInfo?.availableEmailDomains || []}
      initialColumns={initialColumns}
      applySelectedColumns={applySelectedColumns}
      defaultSelectedColumns={defaultSelectedColumnKeys}
      isLoading={isLoading}
      pageNumber={pageNumber}
      rowsPerPage={rowsPerPage}
      total={total !== undefined ? total : tableData.length}
      disablePagination={false}
      disableSearch={true}
      disableFiltersSettings={true}
      // disableColumnSettings={true}
      getCSVData={getCSVData}
      getCSVFileName={getCSVFileName}
      onSendEmail={onSendEmail}
      columnOrder={columnOrder}
      onColumnOrderChange={onColumnOrderChange}
      applySort={handleSortChange}
      onPageChange={handlePageChange}
      onClearSelection={handleClearSelection}
      emptyMessage={emptyMessage}
      containerStyles={{
        backgroundColor: "#FFFFFF",
        borderRadius: "8px",
        border: "1px solid #E6E6E6",
      }}
      getTotalRowSelection={getTotalRowSelection}
      getTotalRowSelectionCount={getTotalRowSelectionCount}
      initialSorting={getCurrentSorting()}
      rowSelection={{
        selectedRowKeys: getCurrentPageSelection(),
        onSelect: onSelectChange,
        onSelectAll: onSelectAllChange
      }}
    />
  );
};

export default ChartDetailsTableSection;
